import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { Evento, GrupoAcceso, ListaDeInvitados, ResumenEscaneos, ResumenInvitados, ResumenVentas } from '../../api/ops/pedidos';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { EntradasNoContratado } from './NoContratado';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { rolesDe } from '../../logica/acceso';
import { dinero } from '../../logica/dinero';
import { estadoDeError } from '../../logica/estados';
import { cifra } from '../../logica/fechasCortas';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import { rutaDeApartado } from '../apartados';
import type { PropsPantalla } from '../tipos';
import { eventoInicial, llegadasPorHora, momentoDe, partesDelAforo, pieInvitados, porcentaje, vendidas, ventasPorCanal, ventasPorTipo, type Barra } from './informes';
import { AYUDA, PERMISOS, cuandoEvento, opsEntradas } from './textos';

/**
 * Informes del evento (10.8): por evento, «Dentro ahora» en vivo (Socio: «Han llegado»; antes, «Vendidas»; terminado,
 * «Llegaron»), aforo con barra apilada, vendidas, ingresos (sin gastos de gestión), sin pagar y reembolsado; ventas por
 * tipo y por canal, llegadas por hora y listas de invitados. Cada gráfico con su tabla (G290), sin minigráficos ni la
 * variación «frente al…» (la API no la da). Solo cifras, sin datos de compradores. Quien puede ver pedidos salta a ellos
 * filtrados; el Socio (solo `entradas.informes.ver`) no. Cada pieza se pide aparte: si Pases o Listas responden 403/404,
 * esa pieza no sale.
 */
interface Datos {
    evento: string;
    ventas: ResumenVentas | null;
    error: unknown;
    grupo: GrupoAcceso | null;
    escaneos: ResumenEscaneos | null;
    invitados: ResumenInvitados | null;
    listas: readonly ListaDeInvitados[];
    tipos: Readonly<Record<string, string>>;
}

export function Informes(props: PropsPantalla) {
    const { negocio, movil, membresia, nombreNegocio, navegar, busqueda } = props;
    const ops = opsEntradas(props.ops);
    const permisos = usePermisos();
    const ver = permisos.puede(PERMISOS.informes);
    const verPedidos = permisos.puede(PERMISOS.pedidosVer);
    const [eventos, setEventos] = useState<readonly Evento[] | null>(null);
    const [errorEventos, setErrorEventos] = useState<unknown>(null);
    const [elegido, setElegido] = useState<string | null>(busqueda.get('evento'));
    const [datos, setDatos] = useState<Datos | null>(null);
    const [porCanal, setPorCanal] = useState(0);
    const [intento, setIntento] = useState(0);
    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        ops.listEvents(negocio, { limit: 100 })
            .then((l) => {
                if (vivo) {
                    setEventos(l.data);
                    setErrorEventos(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setEventos([]);
                    setErrorEventos(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, ver, intento]);

    const ahora = new Date();
    const ev = eventos === null ? undefined : (eventos.find((e) => e.id === elegido) ?? eventoInicial(eventos, elegido, ahora));
    const idEvento = ev?.id ?? elegido;
    const grupoId = ev?.access_group_id ?? null;

    useEffect(() => {
        if (!ver || idEvento === null || eventos === null) {
            return;
        }
        let vivo = true;
        const nada = <T,>(p: Promise<T>): Promise<T | null> => p.catch(() => null);
        void Promise.all([
            ops.getEventSalesSummary(negocio, idEvento).then((v) => ({ v, e: null as unknown })).catch((e: unknown) => ({ v: null, e })),
            grupoId === null ? Promise.resolve(null) : nada(ops.getAccessGroup(negocio, grupoId)),
            grupoId === null ? Promise.resolve(null) : nada(ops.getScanSummary(negocio, grupoId)),
            nada(ops.getEventGuestSummary(negocio, idEvento)),
            nada(ops.listGuestLists(negocio, idEvento)),
            nada(ops.listTicketTypes(negocio, idEvento)),
        ]).then(([ventas, grupo, escaneos, invitados, listas, tipos]) => {
            if (vivo) {
                setDatos({
                    evento: idEvento,
                    ventas: ventas.v,
                    error: ventas.e,
                    grupo,
                    escaneos,
                    invitados,
                    listas: listas?.data ?? [],
                    tipos: Object.fromEntries((tipos?.data ?? []).map((t) => [t.id, t.name ?? ''])),
                });
            }
        });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, ver, idEvento, grupoId, eventos, intento]);

    useCabecera({ ayuda: ver ? AYUDA.informes : [], acciones: [] });

    const sinPermiso = <EstadoSinPermiso seccion="Informes" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    if (!ver) {
        return sinPermiso;
    }
    if (eventos === null) {
        return <EstadoCargando seccion="los informes" />;
    }
    if (idEvento === null) {
        return (
            <div className="rv-ca-vacia">
                <span className="rv-ca-vacia__baldosa"><Icono nombre="chart-column" /></span>
                <strong>Todavía no hay informes</strong>
                <span>{errorEventos === null ? 'Aparecen cuando publicas un evento.' : 'Tu rol no puede elegir el evento: ábrelo desde un enlace a su informe.'}</span>
            </div>
        );
    }
    const d = datos?.evento === idEvento ? datos : null;
    if (d === null) {
        return <EstadoCargando seccion="el informe" />;
    }
    if (d.ventas === null) {
        const e = estadoDeError(d.error);
        if (e.tipo === 'sin-permiso') {
            return sinPermiso;
        }
        if (e.tipo === 'no-contratado') {
            return <EntradasNoContratado propietario={membresia?.is_owner === true} navegar={navegar} movil={movil} />;
        }
        return <EstadoError seccion="el informe" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
    }

    const r = d.ventas;
    const momento = ev === undefined ? 'antes' : momentoDe(ev, ahora);
    const aforo = r.capacity.capacity;
    const vendidasN = vendidas(r);
    const llegadas = r.arrivals;
    const socio = !verPedidos;
    const heroe = (() => {
        if (momento === 'en-vivo' && !socio && d.grupo !== null) {
            return { titulo: 'Dentro ahora', valor: d.grupo.attendance.inside, de: `de ${cifra(aforo)} de aforo`, sub: `${cifra(llegadas)} llegadas · ${String(porcentaje(llegadas, vendidasN))} % de las entradas vendidas` };
        }
        if (momento === 'en-vivo') {
            return { titulo: 'Han llegado', valor: llegadas, de: `de ${cifra(aforo)} de aforo`, sub: 'Se actualiza con cada pase que entra.' };
        }
        if (momento === 'terminado') {
            return { titulo: 'Llegaron', valor: llegadas, de: `de ${cifra(vendidasN + (d.invitados?.people ?? 0))} con entrada o invitación`, sub: '' };
        }
        return { titulo: 'Vendidas', valor: vendidasN, de: `de ${cifra(aforo)} de aforo`, sub: `Quedan ${cifra(r.capacity.available)} plazas libres` };
    })();
    const partes = partesDelAforo(r.capacity, momento === 'terminado');
    const rutaPedidos = verPedidos ? rutaDeApartado(props, 'pedidos_pedidos', 'pedidos_') : null;
    const aPedidos = (filtro: string | null) =>
        rutaPedidos === null ? undefined : () => { navegar(`${rutaPedidos}&evento=${encodeURIComponent(idEvento)}${filtro === null ? '' : `&filtro=${filtro}`}`); };
    const barras: Barra[] = porCanal === 1 ? ventasPorCanal(r) : ventasPorTipo(r, d.tipos);
    const horas = d.escaneos === null ? [] : llegadasPorHora(d.escaneos, ahora);
    const nombreLista = (id: string | undefined) => d.listas.find((l) => l.id === id)?.name ?? 'Lista';

    return (
        <div className="rv-ca-apartado">
            <div className={`rv-pe-contexto${movil ? ' rv-pe-contexto--movil' : ''}`}>
                {eventos.length > 0 && (
                    <Desplegable
                        etiqueta="Evento"
                        valor={idEvento}
                        opciones={eventos.filter((e) => e.status === 'published' || e.status === 'ended' || e.id === idEvento).map((e) => ({ valor: e.id, texto: [e.name ?? 'Evento', cuandoEvento(e, ahora)].filter((x) => x !== '').join(' · ') }))}
                        onChange={setElegido}
                    />
                )}
                <Chip estado={momento === 'en-vivo' ? 'live' : momento === 'terminado' ? 'neutral' : 'confirmed'}>{momento === 'en-vivo' ? 'En vivo' : momento === 'terminado' ? 'Finalizado' : 'Publicado'}</Chip>
                {ev !== undefined && <span className="rv-pe-gris">{cuandoEvento(ev, ahora)}</span>}
            </div>

            <div className={`rv-pe-rejilla${movil ? ' rv-pe-rejilla--movil' : ''}`}>
                <section className="rv-pe-heroe" aria-live={momento === 'en-vivo' ? 'polite' : 'off'}>
                    <span className="rv-pe-heroe__t">{momento === 'en-vivo' && <span className="rv-pe-punto" aria-hidden="true" />}{heroe.titulo}</span>
                    <span className="rv-pe-heroe__cifra"><strong>{cifra(heroe.valor)}</strong><span>{heroe.de}</span></span>
                    {heroe.sub !== '' && <span className="rv-pe-heroe__sub">{heroe.sub}</span>}
                </section>
                <section className="rv-pe-tarjeta rv-pe-tarjeta--ancha">
                    <span className="rv-pe-tarjeta__t">Aforo</span>
                    <span className="rv-pe-cifra"><strong>{cifra(r.capacity.available)}</strong><span>{r.capacity.available === 1 ? 'plaza libre' : 'plazas libres'}</span></span>
                    <span className="rv-pe-gris">{`de ${cifra(aforo)} de aforo`}</span>
                    <div className="rv-pe-apilada" role="img" aria-label={partes.map((p) => `${p.etiqueta}: ${String(p.valor)}`).join(', ')}>
                        {partes.filter((p) => p.valor > 0).map((p) => <span key={p.clave} style={{ flexGrow: p.valor, background: p.color }} />)}
                    </div>
                    <ConTabla etiqueta="Aforo" columnas={['Parte', 'Plazas']} filas={partes.map((p) => [p.etiqueta, cifra(p.valor)])}>
                        <ul className="rv-pe-leyenda">
                            {partes.map((p) => <li key={p.clave}><span style={{ background: p.color }} />{p.etiqueta}<strong>{`${cifra(p.valor)} ${p.valor === 1 ? 'plaza' : 'plazas'}`}</strong></li>)}
                        </ul>
                    </ConTabla>
                </section>
            </div>

            <div className={`rv-pe-cifras${movil ? ' rv-pe-cifras--movil' : ''}`}>
                <Cifra titulo="Vendidas" valor={cifra(vendidasN)} unidad={vendidasN === 1 ? 'entrada' : 'entradas'} enlace={aPedidos(null)} textoEnlace="Pedidos" />
                <Cifra titulo="Ingresos" valor={dinero(r.revenue)} pie="Sin los gastos de gestión" />
                <Cifra titulo="Sin pagar" valor={cifra(r.pending_payment ?? 0)} unidad="pedidos" pie="Plazas bloqueadas 30 min" enlace={aPedidos('sin-pagar')} textoEnlace="Ver" />
                <Cifra titulo="Reembolsado" valor={dinero(r.refunded ?? { amount: 0, currency: r.revenue.currency })} enlace={aPedidos('cancelados')} textoEnlace="Ver" />
            </div>

            <div className={`rv-pe-rejilla rv-pe-rejilla--mitades${movil ? ' rv-pe-rejilla--movil' : ''}`}>
                <section className="rv-pe-tarjeta">
                    <span className="rv-pe-tarjeta__cab">
                        <span className="rv-pe-tarjeta__t">Ventas</span>
                        <Filtros etiqueta="Agrupar las ventas" items={[{ etiqueta: 'Por tipo' }, { etiqueta: 'Por canal' }]} valor={porCanal} onCambio={setPorCanal} />
                    </span>
                    <span className="rv-pe-cifra"><strong>{cifra(vendidasN)}</strong><span>entradas</span></span>
                    {barras.length === 0 ? (
                        <Vacio titulo="Todavía no hay ventas" texto="Aquí verás las entradas vendidas por tipo y por canal." />
                    ) : (
                        <ConTabla etiqueta={porCanal === 1 ? 'Ventas por canal' : 'Ventas por tipo'} columnas={[porCanal === 1 ? 'Canal' : 'Tipo', 'Entradas', '%']} filas={barras.map((b) => [b.etiqueta, cifra(b.valor), `${String(porcentaje(b.valor, vendidasN))} %`])}>
                            <BarrasH barras={barras} unidad="entradas" />
                        </ConTabla>
                    )}
                </section>
                <section className="rv-pe-tarjeta">
                    <span className="rv-pe-tarjeta__t">Llegadas por hora</span>
                    <span className="rv-pe-cifra"><strong>{cifra(horas.reduce((n, h) => n + h.valor, 0))}</strong><span>llegadas</span></span>
                    {momento === 'en-vivo' && horas.length > 0 && <span className="rv-pe-gris">La hora en curso, en rojo</span>}
                    {horas.length === 0 ? (
                        <Vacio titulo="Todavía no hay llegadas" texto="Aparecen el día del evento, al abrir puertas." />
                    ) : (
                        <ConTabla etiqueta="Llegadas por hora" columnas={['Hora', 'Llegadas']} filas={horas.map((h) => [`${h.etiqueta}${h.enCurso ? ' (en curso)' : ''}`, cifra(h.valor)])}>
                            <BarrasV barras={horas} />
                        </ConTabla>
                    )}
                </section>
            </div>

            {d.invitados !== null && (
                <section className="rv-pe-tarjeta">
                    <span className="rv-pe-tarjeta__cab"><span className="rv-pe-tarjeta__t">Listas de invitados</span><span className="rv-pe-gris">{`${cifra(d.invitados.people)} personas`}</span></span>
                    {(d.invitados.by_list ?? []).length === 0 ? (
                        <Vacio titulo="Este evento no tiene listas de invitados." texto="" />
                    ) : (
                        <TablaSimple etiqueta="Personas apuntadas por lista" columnas={['Lista', 'Personas']} filas={(d.invitados.by_list ?? []).map((l) => [nombreLista(l.guest_list_id), cifra(l.people ?? 0)])} />
                    )}
                    {pieInvitados(d.invitados) !== '' && <span className="rv-pe-gris">{pieInvitados(d.invitados)}</span>}
                </section>
            )}
        </div>
    );
}

function Cifra({ titulo, valor, unidad, pie, enlace, textoEnlace }: { titulo: string; valor: string; unidad?: string; pie?: string; enlace?: () => void; textoEnlace?: string }) {
    return (
        <section className="rv-pe-tarjeta rv-pe-kpi">
            <span className="rv-pe-tarjeta__cab">
                <span className="rv-pe-tarjeta__t">{titulo}</span>
                {enlace !== undefined && (
                    <button type="button" className="rv-pe-enlace" onClick={enlace}><span>{textoEnlace}</span><Icono nombre="chevron-right" /></button>
                )}
            </span>
            <span className="rv-pe-cifra"><strong>{valor}</strong>{unidad !== undefined && <span>{unidad}</span>}</span>
            {pie !== undefined && <span className="rv-pe-gris">{pie}</span>}
        </section>
    );
}

/** Un gráfico y su tabla con las mismas cifras (G290): «Ver como tabla» / «Ver como gráfico». */
function ConTabla({ etiqueta, columnas, filas, children }: { etiqueta: string; columnas: readonly string[]; filas: readonly (readonly string[])[]; children: ReactNode }) {
    const [tabla, setTabla] = useState(false);
    return (
        <>
            {tabla ? <TablaSimple etiqueta={etiqueta} columnas={columnas} filas={filas} /> : children}
            <button type="button" className="rv-pe-enlace rv-pe-enlace--izq" aria-pressed={tabla} onClick={() => { setTabla((t) => !t); }}>
                <Icono nombre={tabla ? 'chart-column' : 'table'} />
                <span>{tabla ? 'Ver como gráfico' : 'Ver como tabla'}</span>
            </button>
        </>
    );
}

function TablaSimple({ etiqueta, columnas, filas }: { etiqueta: string; columnas: readonly string[]; filas: readonly (readonly string[])[] }) {
    return (
        <table className="rv-pe-tabla" aria-label={etiqueta}>
            <thead><tr>{columnas.map((c) => <th key={c} scope="col">{c}</th>)}</tr></thead>
            <tbody>{filas.map((f, i) => <tr key={`${f[0] ?? ''}${String(i)}`}>{f.map((c, j) => (j === 0 ? <th key={j} scope="row">{c}</th> : <td key={j}>{c}</td>))}</tr>)}</tbody>
        </table>
    );
}

function BarrasH({ barras, unidad }: { barras: readonly Barra[]; unidad: string }) {
    const max = Math.max(1, ...barras.map((b) => b.valor));
    return (
        <ul className="rv-pe-barras-h">
            {barras.map((b) => (
                <li key={b.clave}>
                    <span className="rv-pe-barras-h__fila"><span>{b.etiqueta}</span><strong>{`${cifra(b.valor)} ${unidad}`}</strong></span>
                    <span className="rv-pe-barras-h__fondo"><span style={{ width: `${String((b.valor / max) * 100)}%` }} /></span>
                </li>
            ))}
        </ul>
    );
}

function BarrasV({ barras }: { barras: readonly (Barra & { enCurso: boolean })[] }) {
    const max = Math.max(1, ...barras.map((b) => b.valor));
    return (
        <div className="rv-pe-barras-v" role="img" aria-label={barras.map((b) => `${b.etiqueta}: ${String(b.valor)}`).join(', ')}>
            {barras.map((b) => (
                <span key={b.clave} className="rv-pe-barras-v__col">
                    <span className="rv-pe-barras-v__n">{cifra(b.valor)}</span>
                    <span className={`rv-pe-barras-v__barra${b.enCurso ? ' rv-pe-barras-v__barra--hoy' : ''}`} style={{ height: `${String((b.valor / max) * 100)}%` }} />
                    <span className="rv-pe-barras-v__h">{b.etiqueta}</span>
                </span>
            ))}
        </div>
    );
}

function Vacio({ titulo, texto }: { titulo: string; texto: string }) {
    return (
        <div className="rv-pe-vacio">
            <strong>{titulo}</strong>
            {texto !== '' && <span>{texto}</span>}
        </div>
    );
}
